import { useCallback, useEffect, useMemo, useRef } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { AiDocumentSource } from "components/ai_chat/types";
import ExpandableContent from "components/document_details/ExpandableContent";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { AnswerMarkdown } from "lib/core_components/ai/AnswerMarkdown";
import { useCitationNavigator } from "lib/core_components/useCitationNavigator";
import {
  DocumentSummaryResult,
  useDocumentSummaryCached,
} from "lib/hooks/api/documents/useDocumentSummaryCached";
import { useDocumentSummaryStart } from "lib/hooks/api/documents/useDocumentSummaryStart";
import { useDocumentSummaryUpdates } from "lib/hooks/api/documents/useDocumentSummaryUpdates";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { AiContextProvider } from "lib/providers/AiContextProvider";
import { AiSourceDocumentType, AiSourceType } from "lib/types/graphQLEnums";

type RawSummarySource = NonNullable<DocumentSummaryResult["sources"]>[number];

function toAiDocumentSource(
  s: RawSummarySource,
  fallback: { id: string; title: string },
): AiDocumentSource {
  return {
    type: AiSourceType.Document,
    id: s.id ?? fallback.id,
    title: s.title || fallback.title,
    documentType:
      (s.documentType as AiSourceDocumentType | null) ?? AiSourceDocumentType.BuyerDocument,
    chunkNumber: s.chunkNumber ?? 0,
    locations: s.locations ?? [],
    citationId: s.citationId ?? undefined,
    filePath: s.filePath ?? undefined,
    isDeleted: s.isDeleted ?? undefined,
  };
}

type DocumentAiSummaryProps = {
  documentId: string;
  documentTitle: string;
  isDocumentLoading: boolean;
};

type SummaryPanelState = "loading" | "error" | "empty" | "ready";

function getSummaryPanelState({
  isLoading,
  isSummaryError,
  hasSummary,
}: {
  isLoading: boolean;
  isSummaryError: boolean;
  hasSummary: boolean;
}): SummaryPanelState {
  if (isLoading) return "loading";
  if (isSummaryError) return "error";
  if (hasSummary) return "ready";
  return "empty";
}

export default function DocumentAiSummary({
  documentId,
  documentTitle,
  isDocumentLoading,
}: DocumentAiSummaryProps) {
  const navigateToCitation = useCitationNavigator();
  const { data: currentUser } = useCurrentUser();

  const {
    data: cached,
    isLoading: isCachedLoading,
    isError: isCachedError,
  } = useDocumentSummaryCached(documentId);
  const {
    mutate: startSummary,
    isLoading: isStarting,
    isError: isStartError,
  } = useDocumentSummaryStart();

  // Starts at most one run per mount; callers key the panel by document, so each document gets
  // its own. A `missing` status after a failure doesn't re-trigger.
  const hasAutoTriggered = useRef(false);

  useEffect(() => {
    if (cached?.status !== "missing" || hasAutoTriggered.current) return;
    hasAutoTriggered.current = true;
    startSummary({ documentId });
  }, [cached?.status, documentId, startSummary]);

  useDocumentSummaryUpdates({
    documentId,
    operationId: cached?.operationId ?? null,
    enabled: cached?.status === "running",
  });

  const aiSummary = cached?.status === "completed" ? cached.result : null;
  const isSummaryError = isCachedError || isStartError || cached?.status === "failed";
  const panelState = getSummaryPanelState({
    isLoading:
      isDocumentLoading ||
      isCachedLoading ||
      isStarting ||
      cached?.status === "running" ||
      (cached?.status === "missing" && !isStartError),
    isSummaryError,
    hasSummary: Boolean(aiSummary?.summary),
  });
  const showRetry = isSummaryError && currentUser?.isAdmin === true;

  const sources: AiDocumentSource[] = useMemo(
    () =>
      (aiSummary?.sources ?? []).map((s) =>
        toAiDocumentSource(s, { id: documentId, title: documentTitle }),
      ),
    [aiSummary?.sources, documentId, documentTitle],
  );

  const handleCitationClick = useCallback(
    (source: AiDocumentSource) => {
      navigateToCitation(source.locations ?? []);
    },
    [navigateToCitation],
  );

  return (
    <Flex column>
      <SummaryTitle>
        <Text h3 bold color="sysTextDefault">
          Your summary
        </Text>
      </SummaryTitle>
      {panelState === "error" && (
        <EmptyState
          width="100%"
          type="loadError"
          heading="Summary unavailable"
          description="A summary couldn't be generated for this document. Please try again later."
          action={
            showRetry ? (
              <Button
                buttonType="secondary"
                onClick={() => startSummary({ documentId })}
                icon="refreshCw01"
              >
                Retry
              </Button>
            ) : undefined
          }
        />
      )}
      {panelState === "empty" && (
        <EmptyState
          width="100%"
          type="noResults"
          heading="Nothing relevant to summarise"
          description="We read through this document and didn't find anything that ties to your team profile."
        />
      )}
      {panelState === "loading" && (
        <Flex column gap={16} aria-label="Generating AI summary">
          <SkeletonText
            width="100%"
            fontSize={32}
            lineHeight={1}
            backgroundColor="sysBorderDecorative"
            customSpacing={true}
            animationDelayMs={0}
            variant="rectangular"
          />
          <SkeletonText
            width="100%"
            fontSize={32}
            lineHeight={1}
            backgroundColor="sysBorderDecorative"
            customSpacing={true}
            animationDelayMs={120}
            variant="rectangular"
          />
          <SkeletonText
            width="100%"
            fontSize={32}
            lineHeight={1}
            backgroundColor="sysBorderDecorative"
            customSpacing={true}
            animationDelayMs={200}
            variant="rectangular"
          />
          <SkeletonText
            width="100%"
            fontSize={32}
            lineHeight={1}
            backgroundColor="sysBorderDecorative"
            customSpacing={true}
            animationDelayMs={250}
            variant="rectangular"
          />
        </Flex>
      )}
      {panelState === "ready" && aiSummary?.summary && (
        <AiContextProvider
          context={{
            resourceId: documentId,
            resourceName: documentTitle,
            resourceType: "DOCUMENT_SINGLE_QA",
          }}
        >
          <ExpandableContent maxLines={MAX_LINES} lineHeight={LINE_HEIGHT_PX}>
            <SummaryText>
              <AnswerMarkdown
                answer={aiSummary.summary}
                sources={sources}
                onDocumentSourceClick={handleCitationClick}
              />
            </SummaryText>
          </ExpandableContent>
        </AiContextProvider>
      )}
    </Flex>
  );
}

const SummaryTitle = styled.div({
  paddingBottom: 12,
});

// 6 lines × 21px (14px font-size × 1.5 line-height)
const LINE_HEIGHT_PX = 21;
const MAX_LINES = 6;

const SummaryText = styled.div({
  lineHeight: `${LINE_HEIGHT_PX}px`,
});
